/**
 * HistoryPanel.jsx  v5
 * - Sin cards de valores actuales (confunden en vista histórica)
 * - Eventos de carga/descarga con histéresis + PAUSA_MAX
 */

import { useState, useEffect } from 'react';
import {
    Box, Chip, CircularProgress, Grid, LinearProgress, Paper,
    Stack, ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme,
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { LineChart } from '@mui/x-charts/LineChart';
import RangoFechas, { mesActual } from './Rangofechas';
import { TANKS, VARDEF } from './Hipergas.utils';

export default function HistoryPanel({ current, apiUrl }) {
    const theme = useTheme();
    const [chartVar, setChartVar] = useState('nivel');
    const [rango, setRango] = useState(() => mesActual());
    const [history, setHistory] = useState([]);
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingEv, setLoadingEv] = useState(false);

    const headers = { 'x-api-key': process.env.REACT_APP_HMI_API_KEY || '' };
    const vd = VARDEF[chartVar];

    // ── Histórico de nivel/temp/presión ───────────────────────────────────────
    useEffect(() => {
        if (!apiUrl) return;
        setLoading(true);
        const url = `${apiUrl}/api/alvear/history?from=${encodeURIComponent(rango.from)}&to=${encodeURIComponent(rango.to)}`;
        fetch(url, { headers })
            .then((r) => r.json())
            .then((d) => setHistory(Array.isArray(d) ? d : []))
            .catch(() => setHistory([]))
            .finally(() => setLoading(false));
    }, [rango, apiUrl]);

    // ── Eventos de todos los tanques en paralelo ──────────────────────────────
    useEffect(() => {
        if (!apiUrl) return;
        setLoadingEv(true);
        const from = encodeURIComponent(rango.from);
        const to = encodeURIComponent(rango.to);

        Promise.all(
            TANKS.map((t) =>
                fetch(`${apiUrl}/api/alvear/eventos/${t.id}?from=${from}&to=${to}`, { headers })
                    .then((r) => r.json())
                    .then((d) => (d.eventos || []).map((ev) => ({ ...ev, tanque: t.label, color: t.color })))
                    .catch(() => [])
            )
        )
            .then((results) => {
                const todos = results
                    .flat()
                    .sort((a, b) => {
                        const pa = a.fechaInicio?.split(' ') || [];
                        const pb = b.fechaInicio?.split(' ') || [];
                        return (pa[0] + pa[1]) > (pb[0] + pb[1]) ? -1 : 1;
                    });
                setEventos(todos);
            })
            .finally(() => setLoadingEv(false));
    }, [rango, apiUrl]);

    // Submuestreo para el gráfico — máx 300 puntos
    const submuestreado = history.length > 300
        ? history.filter((_, i) => i % Math.ceil(history.length / 300) === 0)
        : history;

    // Limpiar ceros (pérdida de señal del sensor) — convertir a null para
    // que el gráfico no dibuje esos puntos en lugar de caer a 0
    const chartData = submuestreado.map((row) => {
        const clean = { ...row };
        TANKS.forEach((t) => {
            if (clean[`TK_${t.id}`] === 0) clean[`TK_${t.id}`] = null;
            if (clean[`TEMP_${t.id}`] === 0) clean[`TEMP_${t.id}`] = null;
            if (clean[`PRESION_${t.id}`] === 0) clean[`PRESION_${t.id}`] = null;
        });
        return clean;
    });

    return (
        <>
            {/* Selector de variable */}
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'flex-start' }} mb={2} gap={1.5}>
                <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{vd.label} · histórico</Typography>
                    <Typography variant="caption" color="text.disabled">
                        {history.length} registros · servicio_italgas.alvear
                    </Typography>
                </Box>
                <ToggleButtonGroup value={chartVar} exclusive onChange={(_, v) => v && setChartVar(v)} size="small">
                    {Object.entries(VARDEF).map(([k, v]) => (
                        <ToggleButton key={k} value={k} sx={{ px: 1.5, fontSize: 11, py: 0.5 }}>
                            {v.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Stack>

            {/* Selector de rango */}
            <Box sx={{ mb: 2 }}>
                <RangoFechas value={rango} onChange={setRango} />
            </Box>

            {/* Gráfico */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, position: 'relative' }}>
                {loading && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}>
                        <LinearProgress sx={{ borderRadius: '4px 4px 0 0' }} />
                    </Box>
                )}
                <Stack direction="row" spacing={1.5} mb={1} flexWrap="wrap">
                    {TANKS.map((t) => (
                        <Stack key={t.id} direction="row" alignItems="center" spacing={0.5}>
                            <Box sx={{ width: 12, height: 2, bgcolor: t.color, borderRadius: 1 }} />
                            <Typography variant="caption" color="text.disabled" fontSize={10}>{t.label}</Typography>
                        </Stack>
                    ))}
                </Stack>
                {chartData.length > 0 ? (
                    <LineChart
                        dataset={chartData}
                        series={TANKS.map((t, i) => ({
                            dataKey: vd.keys[i],
                            label: t.label,
                            color: t.color,
                            showMark: false,
                            curve: 'monotoneX',
                            connectNulls: true,
                        }))}
                        xAxis={[{
                            dataKey: 'fecha',
                            scaleType: 'point',
                            tickLabelStyle: { fontSize: 8 },
                            tickInterval: (_, i) => i % Math.ceil(chartData.length / 8) === 0,
                        }]}
                        yAxis={[{ min: vd.min, max: vd.max, tickLabelStyle: { fontSize: 9 }, label: vd.unit }]}
                        height={220}
                        margin={{ left: 40, right: 16, top: 8, bottom: 24 }}
                        slotProps={{ legend: { hidden: true }, tooltip: { trigger: 'none' } }}
                    />
                ) : (
                    <Stack alignItems="center" py={4}>
                        {loading
                            ? <CircularProgress size={24} />
                            : <Typography variant="body2" color="text.disabled">Sin datos en el período seleccionado</Typography>
                        }
                    </Stack>
                )}
            </Paper>

            {/* Eventos de carga/descarga */}
            <Paper variant="outlined">
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" fontWeight={600} fontSize={11}>
                            Eventos del período
                        </Typography>
                        {loadingEv
                            ? <CircularProgress size={14} />
                            : <Typography variant="caption" color="text.disabled" fontSize={10}>
                                {eventos.length} evento{eventos.length !== 1 ? 's' : ''} detectado{eventos.length !== 1 ? 's' : ''}
                            </Typography>
                        }
                    </Stack>
                </Box>

                {loadingEv ? (
                    <Stack alignItems="center" py={3}>
                        <CircularProgress size={24} />
                        <Typography variant="caption" color="text.disabled" mt={1}>Detectando eventos...</Typography>
                    </Stack>
                ) : eventos.length === 0 ? (
                    <Stack alignItems="center" py={3}>
                        <Typography variant="body2" color="text.disabled">Sin eventos detectados en el período</Typography>
                    </Stack>
                ) : (
                    <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                        {eventos.map((ev, i) => (
                            <Box key={i} sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: 1.5, py: 1,
                                borderBottom: i < eventos.length - 1 ? 1 : 0,
                                borderColor: 'divider',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    {ev.tipo === 'carga'
                                        ? <TrendingUpIcon sx={{ color: 'success.main', fontSize: 18, flexShrink: 0 }} />
                                        : <TrendingDownIcon sx={{ color: 'error.main', fontSize: 18, flexShrink: 0 }} />
                                    }
                                    <Box>
                                        <Stack direction="row" alignItems="center" spacing={0.8} flexWrap="wrap">
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ev.color, flexShrink: 0 }} />
                                            <Typography variant="caption" fontWeight={600} fontSize={11}>
                                                {ev.tanque}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={ev.tipo === 'carga' ? 'CARGA' : 'DESCARGA'}
                                                sx={{
                                                    height: 18, fontSize: 9, fontWeight: 700,
                                                    bgcolor: ev.tipo === 'carga'
                                                        ? alpha(theme.palette.success.main, 0.1)
                                                        : alpha(theme.palette.error.main, 0.1),
                                                    color: ev.tipo === 'carga' ? 'success.main' : 'error.main',
                                                    '& .MuiChip-label': { px: 0.8 },
                                                }}
                                            />
                                            <Typography variant="caption" color="text.disabled" fontSize={10}>
                                                {ev.fechaInicio} → {ev.fechaFin} · {ev.duracion}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="caption" color="text.disabled" fontSize={10}>
                                            Nivel: {ev.nivelInicio}% → {ev.nivelFin}%
                                            &nbsp;({ev.deltaPct > 0 ? '+' : ''}{ev.deltaPct}%)
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                                    <Typography sx={{
                                        fontFamily: "'Orbitron', sans-serif",
                                        fontSize: 16, fontWeight: 700, lineHeight: 1,
                                        color: ev.tipo === 'carga' ? 'success.main' : 'error.main',
                                    }}>
                                        {ev.tipo === 'carga' ? '+' : '-'}{ev.deltaTn}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" fontSize={10}>tn</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>
        </>
    );
}